import { useEffect, useState } from 'react';

/**
 * The dark-mode switcher — three states, because "follow the phone" is the
 * right default and an explicit choice must survive it. The choice lands on
 * <html data-theme> (tokens.css guards both directions) and in localStorage,
 * where the inline script in index.html replays it before first paint so a
 * dark-mode reader never gets a white flash.
 */
type Theme = 'system' | 'light' | 'dark';

const KEY = 'apnakosh-theme';

function stored(): Theme {
  try {
    const t = localStorage.getItem(KEY);
    return t === 'light' || t === 'dark' ? t : 'system';
  } catch {
    return 'system';
  }
}

const OPTIONS: { value: Theme; label: string; glyph: string }[] = [
  { value: 'system', label: 'Follow system theme', glyph: '◐' },
  { value: 'light', label: 'Light theme', glyph: '☀' },
  { value: 'dark', label: 'Dark theme', glyph: '☾' },
];

export function ThemeToggle() {
  const [theme, setTheme] = useState<Theme>(stored);

  useEffect(() => {
    const root = document.documentElement;
    if (theme === 'system') delete root.dataset['theme'];
    else root.dataset['theme'] = theme;
    try {
      if (theme === 'system') localStorage.removeItem(KEY);
      else localStorage.setItem(KEY, theme);
    } catch {
      /* private mode: the toggle still works for this page view */
    }
  }, [theme]);

  return (
    <div className="theme-toggle" role="radiogroup" aria-label="Appearance">
      {OPTIONS.map((o) => (
        <button
          key={o.value}
          role="radio"
          aria-checked={theme === o.value}
          aria-label={o.label}
          title={o.label}
          className={`theme-toggle-btn${theme === o.value ? ' theme-toggle-active' : ''}`}
          onClick={() => setTheme(o.value)}
        >
          <span aria-hidden>{o.glyph}</span>
        </button>
      ))}
    </div>
  );
}
